// Menu.js
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { MenuIcon } from "./Icons";
import styled from "styled-components";

const MenuContainer = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
`;

const MenuDropdown = styled.div`
  position: fixed;
  width: 192px;
  z-index: 1000;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-400, #ced4da);
  background: var(--Colors-Neutral-100, #fff);
  box-shadow: 0px 6px 20px 0px rgba(169, 169, 169, 0.25);
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled.li`
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  color: ${(props) => (props.$color ? props.$color : "#495057")};
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;

  &:hover {
    background: var(--Colors-Neutral-300, #e9ecef);
    border-radius: 12px;
  }

  span {
    display: inline-flex;
    flex-shrink: 0;
    width: 24px; /* whatever margin you need */
    height: 24px;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
  }

  /* make any svg inside fill that box */
  span > svg {
    width: 100%;
    height: 100%;
    /* preserve aspect ratio */
    object-fit: contain;
  }
`;

const Menu = ({ menuItems, trigger, triggerRef }) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: "0px", left: "0px" });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const toggleMenu = () => {
    setMenuOpen((prevOpen) => !prevOpen);
  };

  const handleClickOutside = (event) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target) &&
      !buttonRef.current.contains(event.target)
    ) {
      setMenuOpen(false);
    }
  };

  const handleMenuItemClick = (action) => {
    action(); // Execute the action passed in the menu item
    setMenuOpen(false); // Close the menu after item is clicked
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  useLayoutEffect(() => {
    if (isMenuOpen) {
      calculateMenuPosition();
    }
  }, [isMenuOpen]);

  const calculateMenuPosition = () => {
    if (!menuRef.current || !buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();

    const menuHeight = menuRect.height;
    const menuWidth = menuRect.width;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top, left;

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // Determine vertical position
    if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
      // Enough space below or more space below than above
      top = `${buttonRect.bottom}px`;
    } else {
      // Not enough space below, position above
      top = `${buttonRect.top - menuHeight}px`;
    }

    // Determine horizontal position (align left edge)
    if (viewportWidth - buttonRect.left >= menuWidth) {
      // Enough space to the right
      left = `${buttonRect.left}px`;
    } else {
      // Not enough space, align to the right edge
      left = `${viewportWidth - menuWidth - 22}px`;
    }

    setMenuPosition({ top, left });
  };

  return (
    <MenuContainer>
      <MenuButton ref={buttonRef} onClick={toggleMenu}>
        {!trigger ? <MenuIcon /> : trigger}
      </MenuButton>
      {isMenuOpen && (
        <MenuDropdown
          ref={menuRef}
          $top={menuPosition.top}
          $left={menuPosition.left}
        >
          <MenuList>
            {menuItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => handleMenuItemClick(item.action)}
                $color={item.color} // Pass the color as a prop
              >
                <span>{item.icon}</span>
                {item.label}
              </MenuItem>
            ))}
          </MenuList>
        </MenuDropdown>
      )}
    </MenuContainer>
  );
};

export default Menu;
